/**
 * BlueprintOverride — X-Ray / Blueprint rendering mode.
 *
 * When `active` is true this component:
 *  1. Walks every mesh in the scene and swaps its material for a
 *     thin edge-highlight material (a contrasting wireframe or
 *     normal-based outline).
 *  2. Stashes original materials so they can be restored on deactivation.
 *  3. Adjusts the scene background to a crisp dark blueprint aesthetic.
 *
 * The effect is purely visual and does not touch geometry.
 */
import { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useThree, useFrame } from '@react-three/fiber';

interface BlueprintOverrideProps {
    active: boolean;
}

/* ─── Blueprint material palette ──────────────────────────────────── */

const BLUEPRINT_BG = 0x0e1825;
const BLUEPRINT_LINE_DEFAULT = 0x4ea8d6;     // cyan edge lines
const BLUEPRINT_LINE_ACCENT = 0x8ecae6;       // lighter accent for variety
const BLUEPRINT_FILL = 0x0e1825;               // near-invisible fill
const BLUEPRINT_GLASS_LINE = 0x6ec8f0;         // glass edges — brighter

/**
 * Creates the primary wireframe material used for most meshes.
 */
function createBlueprintWireframe(): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
        color: BLUEPRINT_LINE_DEFAULT,
        wireframe: true,
        transparent: true,
        opacity: 0.72,
        depthWrite: true,
    });
}

/**
 * Creates an edge-highlight material applied as a second render pass for
 * solid areas — gives the blueprint a "filled" body.
 */
function createBlueprintFill(): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
        color: BLUEPRINT_FILL,
        transparent: true,
        opacity: 0.15,
        depthWrite: false,
        side: THREE.FrontSide,
    });
}

/**
 * Creates a special wireframe for glass surfaces.
 */
function createBlueprintGlassWire(): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
        color: BLUEPRINT_GLASS_LINE,
        wireframe: true,
        transparent: true,
        opacity: 0.5,
    });
}

/**
 * Creates the accent wireframe used for structural/accent elements.
 */
function createBlueprintAccentWire(): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
        color: BLUEPRINT_LINE_ACCENT,
        wireframe: true,
        transparent: true,
        opacity: 0.55,
    });
}

/* ─── Stash/restore helpers ──────────────────────────────────────── */

const STASH_KEY = '__bp_original_mat';
const BP_MARKER = '__bp_applied';

type StashedMesh = THREE.Mesh & {
    [STASH_KEY]?: THREE.Material | THREE.Material[];
    [BP_MARKER]?: boolean;
};

function isGlassMaterial(mat: THREE.Material): boolean {
    if (mat instanceof THREE.MeshPhysicalMaterial) {
        // Physical glass uses transmission; the budget fallback on
        // balanced/low tiers (matGlassBudget) is alpha-blended with
        // transmission 0 — catch both so blueprint mode highlights
        // glazing identically across quality tiers.
        return mat.transmission > 0.3 || (mat.transparent && mat.opacity <= 0.5);
    }
    return false;
}

function isAccentMaterial(mat: THREE.Material): boolean {
    if (mat instanceof THREE.MeshBasicMaterial) {
        // Guide/vent materials — preserve their identity
        return mat.wireframe || mat.color.getHex() === 0x1d4ed8;
    }
    if (mat instanceof THREE.MeshStandardMaterial) {
        // Vent materials (blue/red)
        const hex = mat.color.getHex();
        return hex === 0x3b82f6 || hex === 0xef4444;
    }
    return false;
}


/* ─── Component ──────────────────────────────────────────────────── */

export function BlueprintOverride({ active }: BlueprintOverrideProps) {
    const scene = useThree((s) => s.scene);
    const invalidate = useThree((s) => s.invalidate);

    // Shared materials — reused across all meshes.
    const mats = useMemo(() => ({
        wire: createBlueprintWireframe(),
        fill: createBlueprintFill(),
        glass: createBlueprintGlassWire(),
        accent: createBlueprintAccentWire(),
    }), []);

    // Clean up shared materials on unmount
    useEffect(() => () => {
        mats.wire.dispose();
        mats.fill.dispose();
        mats.glass.dispose();
        mats.accent.dispose();
    }, [mats]);

    // Track previous background to restore later
    const prevBgRef = useRef<THREE.Color | THREE.Texture | null>(null);
    const prevBgBlurRef = useRef<number>(0);
    const prevEnvRef = useRef<THREE.Texture | null>(null);
    const wasActiveRef = useRef(false);
    // Track blueprint-created objects so we can remove them
    const gridHelperRef = useRef<THREE.GridHelper | null>(null);

    // Main apply/restore effect
    useEffect(() => {
        if (active && !wasActiveRef.current) {
            // ── Entering blueprint mode ──

            // Stash & override background
            prevBgRef.current = scene.background as THREE.Color | THREE.Texture | null;
            prevBgBlurRef.current = scene.backgroundBlurriness;
            prevEnvRef.current = scene.environment;

            scene.background = new THREE.Color(BLUEPRINT_BG);
            scene.backgroundBlurriness = 0;
            scene.environment = null;

            // Add subtle grid helper
            const grid = new THREE.GridHelper(30, 60, 0x1a3548, 0x112230);
            grid.position.y = -0.005;
            grid.name = '__blueprint_grid';
            scene.add(grid);
            gridHelperRef.current = grid;

            // Traverse and swap materials
            scene.traverse((obj) => {
                if (!(obj instanceof THREE.Mesh)) return;
                const mesh = obj as StashedMesh;
                if (mesh[BP_MARKER]) return; // already swapped

                // Skip the grid and ground plane
                if (mesh.name === '__blueprint_grid') return;

                const origMat = mesh.material;
                mesh[STASH_KEY] = origMat;
                mesh[BP_MARKER] = true;

                // Determine which blueprint material to use
                const singleMat = Array.isArray(origMat) ? origMat[0] : origMat;

                if (!singleMat) {
                    mesh.material = mats.wire;
                    return;
                }

                // Ground plane — make a very subtle grid-like wireframe
                if (mesh.geometry instanceof THREE.PlaneGeometry && mesh.geometry.parameters.width >= 20) {
                    mesh.material = mats.fill;
                    return;
                }

                // Glass surfaces
                if (isGlassMaterial(singleMat)) {
                    mesh.material = mats.glass;
                    return;
                }

                // Accent/guide materials
                if (isAccentMaterial(singleMat)) {
                    mesh.material = mats.accent;
                    return;
                }

                // Default: wireframe
                mesh.material = mats.wire;
            });

            wasActiveRef.current = true;
            invalidate();
        } else if (!active && wasActiveRef.current) {
            // ── Leaving blueprint mode ──

            // Restore background
            if (prevBgRef.current != null) {
                scene.background = prevBgRef.current;
            }
            scene.backgroundBlurriness = prevBgBlurRef.current;
            if (prevEnvRef.current != null) {
                scene.environment = prevEnvRef.current;
            }

            // Remove grid helper
            if (gridHelperRef.current) {
                scene.remove(gridHelperRef.current);
                gridHelperRef.current.geometry.dispose();
                (gridHelperRef.current.material as THREE.Material).dispose();
                gridHelperRef.current = null;
            }

            // Restore materials
            scene.traverse((obj) => {
                if (!(obj instanceof THREE.Mesh)) return;
                const mesh = obj as StashedMesh;
                if (!mesh[BP_MARKER]) return;

                const orig = mesh[STASH_KEY];
                if (orig) {
                    mesh.material = orig;
                }
                delete mesh[STASH_KEY];
                delete mesh[BP_MARKER];
            });

            wasActiveRef.current = false;
            invalidate();
        }
    }, [active, scene, mats, invalidate]);

    // If the scene rebuilds (e.g. new meshes added while in blueprint mode),
    // catch new unswapped meshes each frame.
    useFrame(() => {
        if (!active) return;

        let didSwap = false;
        scene.traverse((obj) => {
            if (!(obj instanceof THREE.Mesh)) return;
            const mesh = obj as StashedMesh;
            if (mesh[BP_MARKER]) return;
            if (mesh.name === '__blueprint_grid') return;

            const origMat = mesh.material;
            mesh[STASH_KEY] = origMat;
            mesh[BP_MARKER] = true;

            const singleMat = Array.isArray(origMat) ? origMat[0] : origMat;

            if (!singleMat) {
                mesh.material = mats.wire;
                didSwap = true;
                return;
            }

            if (mesh.geometry instanceof THREE.PlaneGeometry && mesh.geometry.parameters.width >= 20) {
                mesh.material = mats.fill;
                didSwap = true;
                return;
            }

            if (isGlassMaterial(singleMat)) {
                mesh.material = mats.glass;
                didSwap = true;
                return;
            }

            if (isAccentMaterial(singleMat)) {
                mesh.material = mats.accent;
                didSwap = true;
                return;
            }

            mesh.material = mats.wire;
            didSwap = true;
        });

        if (didSwap) invalidate();
    });

    return null;
}
